import { dayLabel, readDate, timeLabel } from '../../utils/appDate';

/**
 * What the expenses screen shows, worked out from the rows.
 *
 * A port of the phone's `ExpensesCubit._emitLoadedState`: the same total, the
 * same per-category grouping keyed by NAME rather than by id, and the same
 * sort — biggest first. Keyed by name on purpose, because that is what the
 * phone does and it means two categories that were renamed into each other
 * still read as one line rather than two identical ones.
 *
 * Pure, and free of React, so the arithmetic can be tested on its own.
 */

/** 'Unknown' is the phone's word for an expense whose category is missing. */
export const UNCATEGORISED = 'Unknown';

/**
 * The category name off an expense row, however the server sent it.
 *
 * A listed expense arrives with `expenseType` populated. The response to an
 * UPDATE does not populate it, so the same field can be a bare id — in that
 * case there is no name to read and the row falls back to the catch-all,
 * which is why the screen re-attaches the category it already holds instead
 * of trusting the update's echo.
 */
export function categoryNameOf(expense) {
  const type = expense?.expenseType;
  if (type && typeof type === 'object' && type.name) return String(type.name);
  return UNCATEGORISED;
}

/** The category id off a row, for matching a row back to the category list. */
export function categoryIdOf(expense) {
  const type = expense?.expenseType;
  if (!type) return '';
  if (typeof type === 'object') return String(type._id || type.id || '');
  return String(type);
}

export function vendorNameOf(expense) {
  const vendor = expense?.vendor;
  if (vendor && typeof vendor === 'object') return String(vendor.name || '');
  return '';
}

/** Newest first — the order the phone's list is read in. */
export function sortByDateDesc(expenses) {
  return [...(expenses || [])].sort(
    (a, b) => readDate(b?.date).valueOf() - readDate(a?.date).valueOf()
  );
}

/**
 * Total, count, and the per-category breakdown.
 *
 * @returns {{total:number, count:number, categories:Array<{name:string,amount:number,share:number}>}}
 */
export function summarise(expenses) {
  const rows = expenses || [];
  const totals = new Map();
  let total = 0;

  rows.forEach((expense) => {
    const amount = Number(expense?.amount) || 0;
    total += amount;

    const name = categoryNameOf(expense);
    totals.set(name, (totals.get(name) || 0) + amount);
  });

  const categories = [...totals.entries()]
    .map(([name, amount]) => ({
      name,
      amount,
      // Zero rather than NaN when nothing was spent: a share of nothing is
      // not a number, and a bar drawn from NaN disappears silently.
      share: total > 0 ? (amount / total) * 100 : 0,
    }))
    .sort((a, b) => b.amount - a.amount);

  return { total, count: rows.length, categories };
}

/** `$1,240.00` — thousands separated, because these figures get large. */
export function money(amount, mark = '$') {
  const value = Number(amount || 0);
  const body =
    mark +
    Math.abs(value)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return value < 0 ? `−${body}` : body;
}

/** `1.0%`, the precision the phone's report uses. */
export const percent = (share) => `${(Number(share) || 0).toFixed(1)}%`;

/**
 * Expense dates follow the same rule as every other date here.
 *
 * `toIso8601String()` on a LOCAL Dart DateTime carries no offset — it is naive
 * wall-clock text, exactly like the order dates — so it is stored three hours
 * early and has to be read back with `moment.utc`. An earlier version of this
 * file claimed these were real instants and read them with plain `moment`,
 * which printed every expense three hours late.
 *
 * `readDate` in utils/appDate is the single place that rule now lives.
 */
export const expenseDay = (value) => dayLabel(value);
export const expenseTime = (value) => timeLabel(value);
